// src/features/rooms/RoomsTable.tsx
import "ag-grid-enterprise";
import React, { useEffect, useState } from "react";
import { Center, Loader, Box, TextInput, Stack, Badge, Group } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { AgGridReact } from "ag-grid-react";
import type { CellFocusedEvent, ICellRendererParams } from "ag-grid-enterprise";
import type { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSearchParams } from "react-router-dom";

import { getPercentColor } from "../../RoomColoring";
import { useProjectId } from "../../hooks/useProjectId";
import { usePostMultiplacerRoomsQuery } from "../../../openapi";
import { useAgGridReduxState } from "../../hooks/useAgGridReduxState";
import { selectAgGridStateByKey } from "../../agGridStateSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { thunkClickOnSpace } from "../../DesignTab/designThunk";
import { selectSelectedPnsPrefixes } from "../../DesignTab/routing/pnsSelectors";
import { selectActiveRoomColoring } from "../../Room/roomSelectors";
import { ColoredBadgeCellRenderer } from "./ColoredBadgeCellRenderer";
import {
  IValidationColumnMeta,
} from "./roomTableDrofusOccurrenceStandardValidationSelectors";
import {
  ROOMS_TABLE_DEFAULT_COL_DEF,
  selectRoomsTableAllColIds,
  selectRoomsTableColumnDefsWithGroups,
  selectRoomsTableColumnsSignature,
} from "./roomTableSelectors";
import { RoomColoringResultChart } from "../../RoomColoring/RoomColoringResultChart";
import { RoomColoringMenu } from "../../RoomColoring/RoomColoringMenu";

const PNS_REQUEST_DEBOUNCE_MS = 500;

// Status-badge renderer (brukes av dynamiske statuskolonner)
const ValidationStatusBadgeCellRenderer: React.FC<
  ICellRendererParams<IRoomSummary, unknown> & { meta?: IValidationColumnMeta }
> = (params) => {
  const meta = params.meta;

  const label = meta?.valueFormatter
    ? meta.valueFormatter(params.value)
    : params.value == null
      ? "–"
      : String(params.value);

  const fill = meta?.valueToColor?.(params.value);

  // No value -> "Ingen status"
  if (params.value == null || params.value === "") {
    return (
      <Badge size="sm" variant="light" color="gray" style={{ textTransform: "none" }}>
        {label}
      </Badge>
    );
  }

  return (
    <Badge
      size="sm"
      variant="filled"
      style={{
        backgroundColor: fill ?? "#868e96",
        color: "#ffffff",
        textTransform: "none",
      }}
    >
      {label}
    </Badge>
  );
};

/** Prosent-renderer med farge */
const PercentCellRenderer: React.FC<
  ICellRendererParams<IRoomSummary, number | undefined> & {
    getValue?: (row: IRoomSummary) => number | undefined;
  }
> = (params) => {
  const raw =
    typeof params.value === "number"
      ? params.value
      : typeof params.getValue === "function"
        ? params.getValue(params.data as IRoomSummary)
        : undefined;

  const percent = typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : undefined;
  const bg = typeof percent === "number" ? getPercentColor(percent) : undefined;
  const label = typeof percent === "number" ? `${percent} %` : "—";

  const style: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontVariantNumeric: "tabular-nums",
    borderRadius: 6,
    backgroundColor: bg,
    color: "#111",
    fontWeight: 500,
  };

  return (
    <div style={style} title={label}>
      {label}
    </div>
  );
};

/** Klikkbar romnummer-lenke */
const RoomNumberLinkRenderer: React.FC<ICellRendererParams<IRoomSummary>> = (params) => {
  const dispatch = useAppDispatch();
  const roomNumber = params.value as string;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(thunkClickOnSpace(roomNumber));
  };

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{
        color: "#0066cc",
        textDecoration: "none",
        cursor: "pointer",
        fontWeight: 500,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
    >
      {roomNumber}
    </a>
  );
};

export const RoomsTable: React.FC = () => {
  const projectId = useProjectId();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const selectedSpaceNumber = useAppSelector((s) => s.design.selectedSpaceNumber);
  const selectedPnsPrefixes = useAppSelector(selectSelectedPnsPrefixes);
  const [debouncedPnsPrefixes] = useDebouncedValue(selectedPnsPrefixes, PNS_REQUEST_DEBOUNCE_MS);
  const activeRoomColoring = useAppSelector(selectActiveRoomColoring);

  const pnsFromUrl = (() => {
    const repeated = searchParams.getAll("pns").map((x) => x.trim()).filter(Boolean);
    if (repeated.length > 1) return repeated;
    const single = searchParams.get("pns");
    if (!single) return [];
    return single
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  })();

  const shouldBlockInitialRoomsFetch = pnsFromUrl.length > 0 && debouncedPnsPrefixes.length === 0;

  const {
    data: rooms = {},
    isLoading,
    isError,
  } = usePostMultiplacerRoomsQuery(
    shouldBlockInitialRoomsFetch
      ? skipToken
      : {
          body: {
            projectNo: projectId,
            ...(debouncedPnsPrefixes.length > 0 ? { pns: debouncedPnsPrefixes } : {}),
          },
        }
  );

  const rows: IRoomSummary[] = Object.values(rooms ?? {}) as IRoomSummary[];
  const columnDefsWithGroups = useAppSelector(selectRoomsTableColumnDefsWithGroups);
  const allColIds = useAppSelector(selectRoomsTableAllColIds);
  const columnsSignature = useAppSelector(selectRoomsTableColumnsSignature);
  const gridKey = `roomsTable:${projectId}`;
  const saved = useAppSelector(selectAgGridStateByKey(gridKey));
  const hasSavedCols = !!(saved?.columnState && saved.columnState.length > 0);
  const [search, setSearch] = useState(saved?.quickFilter ?? "");

  useEffect(() => {
    if (saved?.quickFilter != null) setSearch(saved.quickFilter);
  }, [saved?.quickFilter]);

  const { gridStateHandlers, setQuickFilterText } = useAgGridReduxState(gridKey, {
    availableColIds: allColIds,
    columnsSignature,
    managePivotMode: false,
  });

  if (isLoading)
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  if (isError) return <div>Kunne ikke laste rom.</div>;

  return (
    <Stack h="100%" w="100%" gap="sm">
      <Group justify="space-between" align="center" wrap="nowrap">
        <TextInput
          style={{ width: 300 }}
          placeholder="Søk i alle kolonner…"
          value={search}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setSearch(v);
            setQuickFilterText(v);
          }}
        />
        <Group gap="sm" align="center" wrap="nowrap">
          <Badge
            variant="filled"
            color="blue"
            w="fit-content"
            size="lg"
            style={{
              fontWeight: 700,
              letterSpacing: 0.2,
              boxShadow: "0 4px 12px rgba(25, 113, 194, 0.28)",
              border: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            Antall rom: {rows.length}
          </Badge>
          <RoomColoringMenu tone="primary" />
        </Group>
      </Group>

      <Box className="ag-theme-quartz" style={{ width: "100%", minHeight: 300, flex: 1 }}>
        <AgGridReact<IRoomSummary>
          rowData={rows}
          columnDefs={columnDefsWithGroups}
          defaultColDef={ROOMS_TABLE_DEFAULT_COL_DEF}
          animateRows
          tooltipShowDelay={150}
          autoSizeStrategy={
            hasSavedCols ? undefined : { type: "fitGridWidth", defaultMinWidth: 110 }
          }
          overlayNoRowsTemplate="<span style='padding:8px;display:inline-block;'>Ingen rom funnet</span>"
          groupHeaderHeight={36}
          headerHeight={56}
          suppressScrollOnNewData
          sideBar={["columns"]}
          components={{
            PercentCellRenderer,
            RoomNumberLinkRenderer,
            ValidationStatusBadgeCellRenderer,
            ColoredBadgeCellRenderer,
          }}
          {...gridStateHandlers}
          getRowStyle={(params) => {
            if (params.data?.roomNumber === selectedSpaceNumber) {
              return {
                background: "rgba(0, 102, 204, 0.12)",
                fontWeight: 600,
                borderLeft: "4px solid #0066cc",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
              };
            }
            return undefined;
          }}
          onCellFocused={(event: CellFocusedEvent) => {
            if (event.rowIndex == null) return;

            const rowNode = event.api.getDisplayedRowAtIndex(event.rowIndex);
            const data = rowNode?.data as IRoomSummary | undefined;

            if (data?.roomNumber) {
              dispatch(thunkClickOnSpace(data.roomNumber));
            }
          }}
        />
      </Box>

      {activeRoomColoring && (
        <RoomColoringResultChart
          style={{
            minHeight: 280,
          }}
        />
      )}
    </Stack>
  );
};
